import React from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PaymentModeSelector from "./PaymentModeSelector";
import { DENSE_FIELD_PROPS } from "../utils/formModalStyles";
import {
  resolveAttachmentUrl as resolveAttachmentUrlUtil,
  getInvoiceAttachmentDisplayName,
  downloadInvoiceAttachment,
  downloadLocalAttachmentFile,
} from "../utils/invoiceAttachmentUtils";

/** Shared INR formatter for invoice UIs. */
export const formatInvoiceInr = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

/** Split GST into sgst/cgst halves (same as Create Invoice). */
export const calcInvoiceGstBreakdown = (subtotal, gstPercentage) => {
  const base = Number(subtotal) || 0;
  const pct = Number(gstPercentage) || 0;
  const gstAmount = base === 0 ? 0 : (base * pct) / 100;
  return {
    gstAmount,
    sgst: base === 0 ? 0 : gstAmount / 2,
    cgst: base === 0 ? 0 : gstAmount / 2,
    totalAmount: base + gstAmount,
  };
};

/** Total from subtotal + manual SGST/CGST amounts (Add Stock forms). */
export const calcManualSgstCgstTotal = (subtotal, sgst, cgst) => {
  const base = Number(subtotal) || 0;
  const s = Number(sgst) || 0;
  const c = Number(cgst) || 0;
  return {
    sgst: s,
    cgst: c,
    gstAmount: s + c,
    totalAmount: base + s + c,
  };
};

const isDecimalAmountInput = (raw) =>
  raw === "" || /^\d*\.?\d*$/.test(String(raw));

const taxInputSx = {
  width: 72,
  "& .MuiInputBase-root": { height: 22, minHeight: 22 },
  "& .MuiOutlinedInput-input": {
    py: "2px",
    px: 0.5,
    fontSize: "0.75rem",
    textAlign: "right",
    height: 18,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: 1,
  },
};

/** Kept for existing form imports; implementation lives in invoiceAttachmentUtils. */
export const resolveAttachmentUrl = resolveAttachmentUrlUtil;

/** Paperclip + filename control (Add Stock / Create Invoice style). */
export const InvoiceAttachmentControl = ({
  attachmentName = "",
  attachmentUrl = "",
  attachmentFile = null,
  onFileChange,
  onDelete,
  disabled = false,
  allowDownload = true,
}) => {
  const name = attachmentName ? String(attachmentName) : "";
  const resolvedUrl =
    attachmentUrl ||
    resolveAttachmentUrlUtil(attachmentName) ||
    (name && !attachmentFile ? name : "");
  const displayName =
    getInvoiceAttachmentDisplayName(resolvedUrl || name) || name;
  const canDownload =
    allowDownload &&
    (attachmentFile instanceof File || Boolean(resolvedUrl));

  const handleDownload = async () => {
    try {
      if (attachmentFile instanceof File) {
        await downloadLocalAttachmentFile(attachmentFile);
      } else if (resolvedUrl) {
        await downloadInvoiceAttachment(resolvedUrl);
      }
    } catch {
      // Errors are toasted in download helpers
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        minHeight: 40,
        px: 0.5,
      }}
    >
      <Tooltip title={name ? "Replace attachment" : "Attach file"}>
        <span>
          <IconButton
            component="label"
            size="small"
            color="primary"
            disabled={disabled}
          >
            <AttachFileIcon fontSize="small" />
            <input type="file" hidden disabled={disabled} onChange={onFileChange} />
          </IconButton>
        </span>
      </Tooltip>
      {name || displayName ? (
        <>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.primary",
            }}
            title={displayName || name}
          >
            {displayName || name}
          </Typography>
          {canDownload && (
            <Tooltip title="Download attachment">
              <IconButton size="small" color="primary" onClick={handleDownload}>
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!disabled && (
            <Tooltip title="Remove attachment">
              <IconButton size="small" onClick={onDelete}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Attachment
        </Typography>
      )}
    </Box>
  );
};

/** Subtotal / GST / Total Payable box — matches Create Invoice image. */
export const InvoiceTotalsBox = ({
  subtotal = 0,
  gstPercentage = 0,
  totalAmount = null,
  muted = false,
  dense = false,
  sx = {},
  /** When true, show editable SGST + CGST amount fields instead of fixed GST %. */
  manualGst = false,
  sgstValue = "",
  cgstValue = "",
  onSgstChange,
  onCgstChange,
  /** Optional Total Discount on the same row as SGST/CGST (Add Stock). */
  discountValue = "",
  onDiscountChange,
  showDiscountInGstRow = false,
}) => {
  const { gstAmount, totalAmount: computedTotal } = calcInvoiceGstBreakdown(
    subtotal,
    gstPercentage
  );
  const manual = calcManualSgstCgstTotal(subtotal, sgstValue, cgstValue);
  const payable =
    totalAmount == null
      ? manualGst
        ? manual.totalAmount
        : computedTotal
      : Number(totalAmount) || 0;
  const rowMb = dense ? 0.4 : 0.75;

  return (
    <Box
      sx={{
        p: dense ? 1 : 1.5,
        backgroundColor: "#f5f5f5",
        borderRadius: 1,
        opacity: muted ? 0.75 : 1,
        ...sx,
      }}
    >
      <Box display="flex" justifyContent="space-between" mb={rowMb}>
        <Typography fontWeight="bold" fontSize="0.8125rem">
          Subtotal (₹):
        </Typography>
        <Typography fontWeight="bold" fontSize="0.8125rem">
          {formatInvoiceInr(subtotal)}
        </Typography>
      </Box>
      {manualGst ? (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mb={rowMb}
          gap={1.5}
          flexWrap="wrap"
        >
          {showDiscountInGstRow ? (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize="0.75rem" sx={{ whiteSpace: "nowrap" }}>
                Total Discount (₹):
              </Typography>
              <TextField
                size="small"
                type="number"
                placeholder="0.00"
                value={discountValue ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!isDecimalAmountInput(raw)) return;
                  onDiscountChange?.(raw);
                }}
                inputProps={{ min: 0, step: "0.01" }}
                sx={taxInputSx}
              />
            </Box>
          ) : null}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography fontSize="0.75rem" sx={{ whiteSpace: "nowrap" }}>
              SGST (₹):
            </Typography>
            <TextField
              size="small"
              type="number"
              placeholder="0.00"
              value={sgstValue ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (!isDecimalAmountInput(raw)) return;
                onSgstChange?.(raw);
              }}
              inputProps={{ min: 0, step: "0.01" }}
              sx={taxInputSx}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography fontSize="0.75rem" sx={{ whiteSpace: "nowrap" }}>
              CGST (₹):
            </Typography>
            <TextField
              size="small"
              type="number"
              placeholder="0.00"
              value={cgstValue ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                if (!isDecimalAmountInput(raw)) return;
                onCgstChange?.(raw);
              }}
              inputProps={{ min: 0, step: "0.01" }}
              sx={taxInputSx}
            />
          </Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between" mb={rowMb}>
          <Typography fontSize="0.8125rem">
            GST @ {gstPercentage}% (₹):
          </Typography>
          <Typography fontSize="0.8125rem">
            {formatInvoiceInr(gstAmount)}
          </Typography>
        </Box>
      )}
      <Divider sx={{ my: dense ? 0.5 : 0.75 }} />
      <Box display="flex" justifyContent="space-between">
        <Typography fontWeight="bold" fontSize="0.875rem" color="primary">
          Total Payable Amount (₹):
        </Typography>
        <Typography fontWeight="bold" fontSize="0.875rem" color="primary">
          {formatInvoiceInr(payable)}
        </Typography>
      </Box>
    </Box>
  );
};

/** Comment / Remarks field. */
export const InvoiceRemarksField = ({
  name = "particulars",
  value = "",
  onChange,
  onBlur,
  disabled = false,
  error = false,
  helperText = "",
  minRows = 2,
}) => (
  <TextField
    fullWidth
    id={name}
    name={name}
    label="Comment / Remarks"
    placeholder="Any additional details"
    value={value}
    onChange={onChange}
    onBlur={onBlur}
    disabled={disabled}
    error={error}
    helperText={helperText}
    multiline
    minRows={minRows}
    size="small"
    margin="dense"
  />
);

/**
 * Standard invoice summary block used across Create Invoice, patient invoice, Add Stock, etc.
 * Layout: optional discount (right) → Totals box → Payment Mode → Remarks (full width, stacked).
 */
export const InvoiceSummarySection = ({
  subtotal = 0,
  gstPercentage = 0,
  totalAmount = null,
  muted = false,
  discountField = null,
  paymentModeValue,
  onPaymentModeChange,
  paymentError = false,
  paymentHelperText = "",
  paymentDisabled = false,
  remarksName = "particulars",
  remarksValue = "",
  onRemarksChange,
  remarksDisabled = false,
  remarksMinRows = 2,
  showPayment = true,
  showRemarks = true,
  attachmentField = null,
  sx = {},
  manualGst = false,
  sgstValue = "",
  cgstValue = "",
  onSgstChange,
  onCgstChange,
  discountValue = "",
  onDiscountChange,
  showDiscountInGstRow = false,
}) => (
  <Grid container spacing={1} sx={sx}>
    {/* When discount is on the SGST/CGST row, skip the separate discount row */}
    {discountField && !showDiscountInGstRow ? (
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          sx={{ mb: 0.5, width: "100%" }}
        >
          {discountField}
        </Box>
      </Grid>
    ) : null}

    <Grid item xs={12}>
      <InvoiceTotalsBox
        subtotal={subtotal}
        gstPercentage={gstPercentage}
        totalAmount={totalAmount}
        muted={muted}
        manualGst={manualGst}
        sgstValue={sgstValue}
        cgstValue={cgstValue}
        onSgstChange={onSgstChange}
        onCgstChange={onCgstChange}
        discountValue={discountValue}
        onDiscountChange={onDiscountChange}
        showDiscountInGstRow={showDiscountInGstRow}
      />
    </Grid>

    {showPayment && (
      <>
        <Grid item xs={12} md={attachmentField ? 6 : 12}>
          <PaymentModeSelector
            value={paymentModeValue}
            onChange={onPaymentModeChange}
            error={paymentError}
            helperText={paymentHelperText}
            disabled={paymentDisabled}
          />
        </Grid>
        {attachmentField ? (
          <Grid item xs={12} md={6}>
            {attachmentField}
          </Grid>
        ) : null}
      </>
    )}

    {showRemarks && (
      <Grid item xs={12}>
        <InvoiceRemarksField
          name={remarksName}
          value={remarksValue}
          onChange={onRemarksChange}
          disabled={remarksDisabled}
          minRows={remarksMinRows}
        />
      </Grid>
    )}
  </Grid>
);

export default {
  formatInvoiceInr,
  calcInvoiceGstBreakdown,
  calcManualSgstCgstTotal,
  resolveAttachmentUrl,
  InvoiceAttachmentControl,
  InvoiceTotalsBox,
  InvoiceRemarksField,
  InvoiceSummarySection,
};
